import React, { PropsWithChildren, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { endOfDay, startOfDay } from 'date-fns';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { GetActingReportByDriverXlsxParams } from '@area-restrita/api-client/model/getActingReportByDriverXlsxParams';
import { getActingReportByDriverXlsx } from '@area-restrita/api-client/endpoints/acting-reports/acting-reports.gen';

import Dialog from '@/components/template/Dialog';
import { DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Icons } from '@/components/icons';
import { Form, FormField } from '@/components/ui/form';
import InlineInput from '@/components/InlineInput';
import UserSelector from '@/components/QuerySelectors/UserSelector';
import CalendarInput from '@/components/CalendarInput';
import { cn } from '@/lib/utils';

const schema = z.object({
  driver: z.object({
    label: z.string(),
    value: z.string().min(1),
  }),
  period: z.object({
    from: z.date(),
    to: z.date(),
  }),
});

type ActingReportSchema = z.infer<typeof schema>;

const ActingReport: React.FC<PropsWithChildren> = ({ children }) => {
  const form = useForm<ActingReportSchema>({
    resolver: zodResolver(schema),
  });

  const byDriverReport = useMutation({
    mutationFn: (params: GetActingReportByDriverXlsxParams) =>
      getActingReportByDriverXlsx(params),
  });

  const handleSubmit = useCallback<SubmitHandler<ActingReportSchema>>(
    async (data) => {
      const response = await byDriverReport.mutateAsync({
        driverId: data.driver.value,
        from: startOfDay(data.period.from).toISOString(),
        to: endOfDay(data.period.to).toISOString(),
      });

      window.open(URL.createObjectURL(response), '_blank');
    },
    [byDriverReport],
  );

  return (
    <Dialog
      title="Gerar relatório"
      description="Gerar um relatório das atuações."
      trigger={children}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div className={cn('grid gap-4 py-4')}>
            <FormField
              control={form.control}
              name="driver"
              render={({ field }) => (
                <InlineInput
                  title="Motorista"
                  as={UserSelector}
                  value={field.value}
                  onChange={field.onChange}
                  status={['ACTIVE']}
                  roles={['MOTORISTA']}
                />
              )}
            />

            <FormField
              control={form.control}
              name="period"
              render={({ field }) => (
                <InlineInput
                  title="Período de atuação"
                  as={CalendarInput}
                  mode="range"
                  // max={31}
                  selected={field.value}
                  onSelect={field.onChange}
                  initialFocus
                />
              )}
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting && (
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
              )}
              Gerar relatório
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </Dialog>
  );
};

export default ActingReport;
